import React, { useEffect, useState, useRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import i18next from "i18next";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { DropdownWithSearch, CustomCheckBox } from "hesus-ui-kit";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import { handleApiError } from "@utils/errorHandler";
import axios from "axios";
import * as moment from "moment";
import DatePicker from "react-multi-date-picker";
import Header from "./components/Header";
import ListingData from "./components/ListingData";
import BulkGeneration from "../BulkGeneration";
import PaginationData from "@shared/baseUI/PaginationData/PaginationData";

import {
  InvoiceListContainer,
  OuterWrapper,
  FlexRow,
  SectionContainer,
  FilterFlexRow,
  FilterOuter,
  InvoiceServiceInfo,
  GridRow,
  HeaderWrapperTable,
  ButtonWrapper,
  ActionOuter,
  FilterItem,
  FilterLabel,
  FilterContent,
  Title,
} from "./InvoiceList.styled";
import "./InvoiceList.scss";
import { ColumnButton, labelStyles } from "@utils/extra-style";
import UserInfo from "@utils/UserInfo";
import { invoiceDocumentTypes, filterInvoiceTypes } from "@utils/filterOptions";
import NotFound from "@components/Errors/NotFound";

/**
 * Invoice List,
 * Path - /invoice/list
 * @see See [Invoice Requirements](https://hesusstore.atlassian.net/wiki/spaces/HS/pages/3637346305/Invoices)
 * @component
 */
const InvoiceList = (props) => {
  const { t, entityId, entityType, entityPlatformCountryId } = props;
  const history = useNavigate();
  const location = useLocation();
  const params = useParams();
  const basePath = location.pathname.split("/").slice(0, 2).join("/") + "/";
  const validPaths = ["list"];
  const documentTypes = invoiceDocumentTypes(t);
  const isFirstRender = useRef(true);
  const [isLoading, setIsLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageStart, setPageStart] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pageCount, setPageCount] = useState(0);
  const [counter, setCounter] = useState(0);
  const [isBulkPopupVisible, setIsBulkPopupVisible] = useState(false);
  const countryList = UserInfo.countryListForFilter(t);

  const invoiceTypes = filterInvoiceTypes(t);

  const userMainCountry = UserInfo.countryInfo();

  const today = new Date();
  const firstDayOfLastMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    1
  );
  const [filters, setFilters] = useState({
    startDate: firstDayOfLastMonth,
    endDate: "",
    country: [
      {
        value: userMainCountry?.country.id,
        label: t(userMainCountry?.country?.name),
      },
    ],
    documentIds: [1],
    invoiceTypeIds: [1, 2, 3, 4],
  });

  const [inputFilters, setInputFilters] = useState({
    document: "",
    client: "",
    site: "",
    status: [],
  });

  const [sort, setSort] = useState({
    field: "",
    direction: "",
  });

  const formatDate = (date) => {
    if (!date) return "";
    return moment(date).format("YYYY-MM-DD");
  };

  const fetchData = () => {
    setIsLoading(true);
    // const countryIds = (filters.country ?? [])
    //   .filter((item) => item.value !== "*")
    //   .map((item) => item.value);
    const countryIds =
      entityType === "main"
        ? (filters.country ?? [])
            .filter((item) => item.value !== "*")
            .map((item) => item.value)
        : entityPlatformCountryId
        ? [entityPlatformCountryId]
        : [];

    const statusIds = (inputFilters.status ?? [])
      .filter((item) => item.value !== "")
      .map((item) => item.value);

    axios
      .get("/clientInvoices", {
        params: {
          dateFrom: formatDate(filters.startDate),
          dateTo: formatDate(filters.endDate),
          countries: countryIds ? JSON.stringify(countryIds) : [],
          ...(entityType === "company" && entityId && { companyId: entityId }),
          ...(entityType === "site" && entityId && { siteId: entityId }),
          documentTypes: filters.documentIds
            ? JSON.stringify(filters.documentIds)
            : [],
          invoiceTypes: filters.invoiceTypeIds
            ? JSON.stringify(filters.invoiceTypeIds)
            : [],
          documentNumberSearch: inputFilters.document,
          clientSearch: inputFilters.client,
          siteSearch: inputFilters.site,
          status: statusIds ? JSON.stringify(statusIds) : [],
          orderColumn: sort.field,
          orderBy: sort.direction,
          limit: pageSize,
          page: currentPage,
        },
      })
      .then((response) => {
        if (response?.data?.invoices?.length > 0) {
          setInvoiceData(response.data.invoices);
          setPageCount(response.data.filteredRecords);
        } else {
          setInvoiceData([]);
          setPageCount(0);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        setInvoiceData([]);
        setPageCount(0);
        handleApiError(error);
      });
  };

  const updateFilter = (key, valueOrUpdater) => {
    setFilters((prev) => {
      const newValue =
        typeof valueOrUpdater === "function"
          ? valueOrUpdater(prev[key] ?? [])
          : valueOrUpdater;

      return {
        ...prev,
        [key]: newValue,
      };
    });
  };

  const onFilter = () => {
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, [sort, counter, currentPage, pageSize, pageStart]);

  const handleSort = (field) => {
    let direction = "asc";
    if (sort.field === field) {
      if (sort.direction === "asc") {
        direction = "desc";
      } else if (sort.direction === "desc") {
        direction = "";
      }
    }
    const updatedSort = {
      field: direction ? field : "",
      direction,
    };
    setSort(updatedSort);
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }
    const timeout = setTimeout(() => {
      const shouldSearch = [
        inputFilters.document,
        inputFilters.client,
        inputFilters.site,
      ].every((value) => value.length === 0 || value.length >= 2);

      if (shouldSearch) {
        fetchData();
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [inputFilters.document, inputFilters.client, inputFilters.site]);

  const handleColumnSearch = (field, value) => {
    const updated = {
      ...inputFilters,
      [field]: value,
    };

    setInputFilters(updated);

    if (typeof value !== "string") {
      setCounter(counter + 1);
    }
  };
  const addNewInvoice = () => {
    history(`/invoice/create`);
  };
  const bulkInvoice = () => {
    setIsBulkPopupVisible(true);
  };

  if (
    basePath === "/invoice/" &&
    !validPaths.includes(params.path) 
  ) {
    return <NotFound />;
  }

  return (
    <InvoiceListContainer
      className="invoice-list"
      marginTop={entityType === "main" ? "30px" : "0px"}
    >
      {entityType === "main" && <Title>{t("CLIENT_INVOICING_TITLE")}</Title>}
      <FlexRow
        justifyContent={entityType === "main" ? "end" : "space-between"}
        marginRight="0px"
        padding={entityType === "main" ? "0px" : "1rem 0 0 1rem"}
        alignItems={entityType === "main" ? "normal" : "center"}
      >
        {entityType !== "main" && (
          <Title
            style={{
              width: "auto",
              margin: 0,
              textAlign: "left",
            }}
          >
            {entityType === "site" && t("SITE_INVOICING_TITLE")}
            {entityType === "company" && t("COMPANY_INVOICING_TITLE")}
          </Title>
        )}

        <FlexRow>
          {entityType === "main" && (
            <ButtonWrapper>
              <ColumnButton
                border="#1bace4"
                background="#1bace4"
                padding="10px"
                onClick={() => bulkInvoice()}
              >
                {t("CLIENT_INVOICING_ACTION_BULK_GENERATION")}
              </ColumnButton>
            </ButtonWrapper>
          )}

          <ButtonWrapper>
            <ColumnButton
              border="#1bace4"
              background="#1bace4"
              padding="10px"
              onClick={() => addNewInvoice()}
            >
              {t("CLIENT_INVOICING_ACTION_NEW_INVOICE")}
            </ColumnButton>
          </ButtonWrapper>
        </FlexRow>
      </FlexRow>
      <OuterWrapper>
        <FlexRow>
          <SectionContainer>
            <div>
              <FilterFlexRow>
                <FilterOuter>
                  {entityType === "main" && countryList?.length > 1 && (
                    <DropdownWithSearch
                      id="invoice-country"
                      label={t("COUNTRY")}
                      name="country"
                      value={filters.country}
                      isMulti
                      onChange={(country) => updateFilter("country", country)}
                      defaultOption={t("COUNTRY")}
                      options={countryList}
                      labelStyles={labelStyles}
                      containerClass="invoice-list__dropdown-container"
                    />
                  )}
                  <FilterItem
                    label={t("CLIENT_INVOICING_FILTER_DOCUMENT_TYPE")}
                  >
                    <FilterLabel>
                      {t("CLIENT_INVOICING_FILTER_DOCUMENT_TYPE")}
                    </FilterLabel>
                    <FilterContent>
                      {documentTypes.map((document) => (
                        <CustomCheckBox
                          key={"document" + document.value}
                          id={"document" + document.value}
                          labelText={document.label}
                          checked={filters.documentIds?.includes(
                            document.value
                          )}
                          onChange={(e) =>
                            updateFilter("documentIds", (current) =>
                              e.target.checked
                                ? [...current, document.value]
                                : current.filter((id) => id !== document.value)
                            )
                          }
                        />
                      ))}
                    </FilterContent>
                  </FilterItem>
                  <div className="invoice-list__invoice-date-container">
                    <label htmlFor="invoice-startdate">
                      {t("CLIENT_INVOICING_FILTER_DATE_FROM")}
                    </label>

                    <DatePicker
                      inputClass="invoice-list__invoice-date-input"
                      containerClassName="invoice-list__invoice-datepicker-container"
                      calendarPosition="bottom-center"
                      onChange={(e) =>
                        updateFilter(
                          "startDate",
                          moment(e.toDate()).format("YYYY-MM-DD")
                        )
                      }
                      format={getDatePickerFormat()}
                      locale={getCalendarLocale()}
                      value={
                        getDateFormat(filters.startDate, "YYYY-MM-DD") || ""
                      }
                      name="invoice-startdate"
                    />
                  </div>
                  <div className="invoice-list__invoice-date-container">
                    <label htmlFor="invoice-enddate">
                      {t("CLIENT_INVOICING_FILTER_DATE_TO")}
                    </label>

                    <DatePicker
                      inputClass="invoice-list__invoice-date-input"
                      containerClassName="invoice-list__invoice-datepicker-container"
                      calendarPosition="bottom-center"
                      format={getDatePickerFormat()}
                      locale={getCalendarLocale()}
                      value={getDateFormat(filters.endDate, "YYYY-MM-DD") || ""}
                      onChange={(e) =>
                        updateFilter(
                          "endDate",
                          moment(e.toDate()).format("YYYY-MM-DD")
                        )
                      }
                      name="invoice-enddate"
                    />
                  </div>
                  <FilterItem label={t("CLIENT_INVOICING_FILTER_INVOICE_TYPE")}>
                    <FilterLabel>
                      {t("CLIENT_INVOICING_FILTER_INVOICE_TYPE")}
                    </FilterLabel>
                    <FilterContent>
                      {invoiceTypes.map((invoiceType) => (
                        <CustomCheckBox
                          key={"invoiceType" + invoiceType.value}
                          id={"invoiceType" + invoiceType.value}
                          labelText={invoiceType.label}
                          checked={filters.invoiceTypeIds?.includes(
                            invoiceType.value
                          )}
                          onChange={(e) =>
                            updateFilter("invoiceTypeIds", (current) =>
                              e.target.checked
                                ? [...current, invoiceType.value]
                                : current.filter(
                                    (id) => id !== invoiceType.value
                                  )
                            )
                          }
                        />
                      ))}
                    </FilterContent>
                  </FilterItem>
                </FilterOuter>
                <ActionOuter>
                  <ButtonWrapper>
                    <ColumnButton
                      border="#1bace4"
                      background="#fff"
                      color="#1bace4"
                      padding="10px"
                      onClick={() => onFilter()}
                    >
                      {t("CLIENT_INVOICING_FILTER_ACTION_BUTTON_VALIDATE")}
                    </ColumnButton>
                  </ButtonWrapper>
                </ActionOuter>
              </FilterFlexRow>
            </div>
            <InvoiceServiceInfo>
              <Header
                inputFilters={inputFilters}
                handleSort={handleSort}
                sort={sort}
                handleColumnSearch={handleColumnSearch}
                entityType={entityType}
              />
            </InvoiceServiceInfo>
            <InvoiceServiceInfo>
              <GridRow marginBottom="0px" gap="0rem">
                <HeaderWrapperTable>
                  <ListingData
                    isLoading={isLoading}
                    invoiceData={invoiceData}
                    setCounter={setCounter}
                    counter={counter}
                    entityId={entityId}
                    entityType={entityType}
                  />
                </HeaderWrapperTable>
              </GridRow>
            </InvoiceServiceInfo>
          </SectionContainer>
        </FlexRow>
        {invoiceData?.length !== 0 && (
          <PaginationData
            pageCount={parseInt(pageCount, 10)}
            pageSize={pageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageStart={pageStart}
            setPageStart={setPageStart}
            setPageSize={setPageSize}
          />
        )}
      </OuterWrapper>
      {isBulkPopupVisible && (
        <BulkGeneration onClose={() => setIsBulkPopupVisible(false)} />
      )}
    </InvoiceListContainer>
  );
};

InvoiceList.propTypes = {
  t: PropTypes.func.isRequired,
  page: PropTypes.string,
  entityId: PropTypes.number,
  entityType: PropTypes.string,
  entityPlatformCountryId: PropTypes.number,
};

InvoiceList.defaultProps = {
  page: "main",
  entityId: null,
  entityType: "main",
  entityPlatformCountryId: null,
};

export default withTranslation()(InvoiceList);
